import { yupResolver } from '@hookform/resolvers/yup';
import { ContentCopy, Download } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Card, Grid, Link, Skeleton, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
	useDisbursement,
	useUpdateDisbursementMutation,
} from '../../../api/DisbursementsApi';
import {
	MULTI_CURRENCY_FEATURE_ENABLE,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	UI_MULTI_CURRENCY_FEATURE_ENABLE,
	useAuthContext,
} from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { usePolicyContext } from '../../../context/policy';
import { CurrencyCode } from '../../../types/currencyCode';
import { Disbursement } from '../../../types/disbursements';
import { formatCurrency } from '../../../util/util';
import DisplayCard from '../../DisplayCard';
import DropdownButton, { HiddenActionItem } from '../../DropdownButton';
import Error from '../../Error';
import { FastForm } from '../../fastForm';
import { FORM_NAME } from '../../fastForm/data/constants';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import getDisbursementForm, {
	DisbursementFormValues,
} from '../../fastForm/formBuilders/getDisbursementForm';
import { getValidationSchemaWithoutResolver } from '../../fastForm/utils/getValidationSchema';
import { infoToast } from '../../Toast';
import ActiveLastBreadcrumb from '../components/Breadcrumbs';
import DisbursementStateChip from '../components/DisbursementStateChip';
import DiscardDisbursementModal from '../components/DiscardDisbursementModal';
import {
	COPY_DISBURSEMENT_LOCATOR,
	COPY_DISBURSEMENT_LOCATOR__SUCCESS,
	DISBURSEMENT_PAGE__AMOUNT_TITLE,
	DISBURSEMENT_PAGE__DATE_CREATED_TITLE,
	DISBURSEMENT_PAGE__DATE_UPDATED_TITLE,
	DISBURSEMENT_PAGE__DISBURSEMENT_NOT_FOUND,
	DISBURSEMENT_PAGE__DISBURSEMENT_NOT_FOUND_ACTION_TEXT,
	DISBURSEMENT_PAGE__DISCARD_BUTTON,
	DISBURSEMENT_PAGE__EDIT_BUTTON,
	DISBURSEMENT_PAGE__POLICYHOLDER_TITLE,
	DISBURSEMENT_PAGE__POLICY_TITLE,
	DISBURSEMENT_PAGE__STATUS_TITLE,
	DISBURSEMENT_PAGE__TITLE,
	DOWNLOAD_DISBURSEMENT_DOCUMENT,
} from '../data/constants';
import { disbursementSchema } from '../lib/disbursementSchema';

interface DisbursementPageProps {
	disbursement: Disbursement;
	disableBreadcrumb: boolean;
}

const DisbursementPage = (props: DisbursementPageProps) => {
	const authData = useAuthContext();
	const config = useConfigContext();
	const policyData = usePolicyContext();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [isDiscardDisbursementModalOpen, setIsDiscardDisbursementModalOpen] =
		useState(false);
	const { timezone, authorizationToken } = authData.auth;
	const { disbursement, disableBreadcrumb } = props;
	const multiCurrencyEnabled =
		authData?.auth?.configProperties?.[MULTI_CURRENCY_FEATURE_ENABLE] &&
		authData?.auth?.configProperties?.[UI_MULTI_CURRENCY_FEATURE_ENABLE];
	const policyLocator = disbursement?.policyLocator;
	const policyholderLocator = disbursement?.policyholderLocator;
	const createdTimestamp = disbursement?.createdTimestamp;
	const updatedTimestamp = disbursement?.updatedTimestamp;
	const formattedAmount = formatCurrency(
		parseFloat(disbursement.amount),
		disbursement?.amountCurrency as CurrencyCode,
	);

	const displayId = disbursement?.displayId;
	const state = disbursement?.state;

	const disbursementFields =
		config.cashManagementConfig?.configuration?.disbursement?.fields;

	const form = getDisbursementForm(
		{
			disbursement: disbursement,
			fields: disbursementFields,
			fieldValues: disbursement?.fieldValues || {},
			mediaByLocator: {},
			timezone,
			multiCurrencyEnabled,
		},
		FORM_NAME.disbursement,
	);

	const disbursementFormValues = getDefaultPolicyValues(form, authData);

	const specialFieldValues = {
		amount: disbursement?.amount || '',
		amountCurrency: disbursement?.amountCurrency || '',
		policyholderLocator: disbursement?.policyholderLocator || '',
		policyLocator: disbursement?.policyLocator || '',
	};

	const defaultValues = {
		disbursement: {
			...(disbursementFormValues.disbursement as object),
			...specialFieldValues,
		},
	} as DisbursementFormValues;

	const methods = useForm<DisbursementFormValues>({
		defaultValues,
		mode: 'onBlur',
		reValidateMode: 'onChange',
		shouldFocusError: true,
		resolver:
			form &&
			yupResolver(
				getValidationSchemaWithoutResolver(form).concat(disbursementSchema()),
			),
	});

	const { reset, getValues } = methods;
	const formValues = getValues();

	const updateDisbursement = useUpdateDisbursementMutation({
		disbursementLocator: disbursement.locator,
		authToken: authorizationToken,
		reset,
		formValues,
		queryClient,
	});

	const handleDiscardDisbursement = () => {
		updateDisbursement.mutate({
			action: 'discard',
		});
	};

	const formattedModifiedTimestamp =
		authData.getZonedFormattedDateString(updatedTimestamp);

	const formattedCreatedTimestamp =
		authData.getZonedFormattedDateString(createdTimestamp);

	const hiddenActions: HiddenActionItem[] = [];

	const discardAction = {
		label: DISBURSEMENT_PAGE__DISCARD_BUTTON,
		callback: () => setIsDiscardDisbursementModalOpen(true),
		icon: <DeleteIcon />,
	};

	const editAction = {
		label: DISBURSEMENT_PAGE__EDIT_BUTTON,
		callback: () => navigate('edit'),
		icon: <EditIcon />,
	};

	const downloadAction = {
		label: DOWNLOAD_DISBURSEMENT_DOCUMENT,
		callback: () => {
			window.open(disbursement.documents[0].url, '_blank');
		},
		icon: <Download />,
	};

	const copyLocatorAction = {
		label: COPY_DISBURSEMENT_LOCATOR,
		callback: () => {
			navigator.clipboard.writeText(disbursement.locator);
			infoToast(COPY_DISBURSEMENT_LOCATOR__SUCCESS);
		},
		icon: <ContentCopy />,
	};

	if (state === 'draft' && authData.hasPermission('disbursement.update')) {
		hiddenActions.push(editAction);
		hiddenActions.push(discardAction);
	}

	if (state === 'issued' && disbursement.documents?.length) {
		hiddenActions.push(downloadAction);
	}

	if (disbursement.locator) {
		hiddenActions.push(copyLocatorAction);
	}

	return (
		<FormProvider {...methods}>
			<Stack spacing={2} alignItems='flex-start'>
				{!disableBreadcrumb && <ActiveLastBreadcrumb />}
				<Stack
					direction='row'
					justifyContent='space-between'
					alignItems='center'
					sx={{ width: '100%' }}
				>
					<Stack direction='row' spacing={2} alignItems='center'>
						<Typography variant='h3'>
							{DISBURSEMENT_PAGE__TITLE} {displayId}
						</Typography>
					</Stack>
					<DropdownButton actions={hiddenActions} />
				</Stack>
				<Stack direction='row' spacing={2} alignItems='center'>
					<DisplayCard
						title={DISBURSEMENT_PAGE__AMOUNT_TITLE}
						value={formattedAmount}
					/>
					<DisplayCard
						title={DISBURSEMENT_PAGE__POLICYHOLDER_TITLE}
						value={policyholderLocator}
					/>
					<DisplayCard
						title={DISBURSEMENT_PAGE__POLICY_TITLE}
						value={
							<Link component={RouterLink} to={`/policy/${policyLocator}`}>
								{policyLocator || '-'}
							</Link>
						}
					/>
					<DisplayCard
						title={DISBURSEMENT_PAGE__DATE_CREATED_TITLE}
						value={formattedCreatedTimestamp}
					/>
					<DisplayCard
						title={DISBURSEMENT_PAGE__DATE_UPDATED_TITLE}
						value={formattedModifiedTimestamp}
					/>
					<DisplayCard
						title={DISBURSEMENT_PAGE__STATUS_TITLE}
						value={<DisbursementStateChip state={state} />}
					/>
				</Stack>
				<Card sx={{ width: '100%', p: 0 }}>
					<Grid container spacing={2} sx={{ pt: 2, pl: 2, pb: 2 }}>
						<FastForm
							formName={FORM_NAME.disbursement}
							form={form}
							productName={policyData?.productConfig?.name}
							configVersion=''
							authData={authData}
							disabled
						/>
					</Grid>
				</Card>
			</Stack>
			<DiscardDisbursementModal
				open={isDiscardDisbursementModalOpen}
				onClose={() => setIsDiscardDisbursementModalOpen(false)}
				onConfirm={() => {
					handleDiscardDisbursement();
					setIsDiscardDisbursementModalOpen(false);
				}}
			/>
		</FormProvider>
	);
};

const DisbursementPageLoadingSkeleton = () => {
	return (
		<Stack spacing={2} alignItems='flex-start'>
			<ActiveLastBreadcrumb loading />
			<Stack direction='row' spacing={2} alignItems='center'>
				<Typography variant='h3'>
					<Skeleton variant='text' width={80} />
				</Typography>
			</Stack>
			<Stack direction='row' spacing={2} alignItems='center'>
				<DisplayCard title={DISBURSEMENT_PAGE__POLICYHOLDER_TITLE} loading />
				<DisplayCard title={DISBURSEMENT_PAGE__POLICY_TITLE} loading />
				<DisplayCard title={DISBURSEMENT_PAGE__DATE_CREATED_TITLE} loading />
				<DisplayCard title={DISBURSEMENT_PAGE__DATE_UPDATED_TITLE} loading />
				<DisplayCard title={DISBURSEMENT_PAGE__STATUS_TITLE} loading />
			</Stack>
			<Card sx={{ width: '100%', height: 96 }}></Card>
		</Stack>
	);
};

const DisbursementPageWrapper = () => {
	const { disbursementLocator, policyholderId, policyId } = useParams();
	const navigate = useNavigate();
	const authData = useAuthContext();
	const authToken = authData.auth?.authorizationToken;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];

	const {
		data: disbursement,
		isLoading,
		isError,
		error,
	} = useDisbursement(
		disbursementLocator,
		authToken,
		cashManagementEnabled,
		authData.hasPermission('disbursement.fetch'),
	);

	if (isError && axios.isAxiosError(error)) {
		return (
			<Error
				error={
					error?.response?.data.message ||
					`${DISBURSEMENT_PAGE__DISBURSEMENT_NOT_FOUND} ${disbursementLocator}`
				}
				primaryAction={() => navigate('/disbursements')}
				primaryActionText={
					DISBURSEMENT_PAGE__DISBURSEMENT_NOT_FOUND_ACTION_TEXT
				}
			/>
		);
	}

	if (isLoading) {
		return <DisbursementPageLoadingSkeleton />;
	}

	return (
		<DisbursementPage
			disbursement={disbursement}
			disableBreadcrumb={!!policyholderId || !!policyId}
		/>
	);
};

export default DisbursementPageWrapper;
